import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { createFile, fetchFiles } from "../redux/actions/fileActions";
import CustomAutocomplete from "../components/CustomAutocomplete";

import { fetchRelationships } from "../redux/actions/relationshipActions";
import {
  Container,
  Snackbar,
  Alert,
  Tabs,
  Autocomplete,
  Tab,
  Box,
  TextField,
  Button,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  FormControl,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormHelperText,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
// import File from "../../../server/models/File";
import { textFieldStyles } from "../styles/textFieldStyles";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { verticalTabStyles } from "../styles/verticalTabStyles";
import CancelButton from "../components/CancelButton";
import SubmitButton from "../components/SubmitButton";
// Breadcrumbs will be set in component with translation
const buttonStyles = {
  borderRadius: 1, // Rounded corners
  padding: "6px 16px", // Custom padding
  textTransform: "none", // Disable uppercase text
  boxShadow: "none", // Remove default shadow
  "&:hover": {
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)", // Add custom shadow on hover
  },
};

const inputStyles = {
  width: "30%",
  marginBottom: 2,
};

const CreateFilePage = () => {
  const { t } = useTranslation(['pages', 'forms', 'buttons', 'common', 'navigation']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const relationships = useSelector(
    (state) => state.relationshipList.relationships
  );
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state
  const [userRole, setUserRole] = useState(null);
  const [userRelationship, setUserRelationship] = useState(null);
  const [selectedRelationships, setSelectedRelationships] = useState([]);
  const [selectedParties, setSelectedParties] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isRestoringState, setIsRestoringState] = useState(false);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [reference, setReference] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createdOn, setCreatedOn] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [language, setLanguage] = useState("English");

  const breadcrumbs = [
    { label: t('navigation:home'), href: "/" },
    { label: t('navigation:files'), href: "/files" },
  ];
  const [categories, setCategories] = useState("");
  const [opposingPartyFileNumber, setOpposingPartyFileNumber] = useState("");
  const [officeMembers, setOfficeMembers] = useState([
    { role: "Responsible Lawyer", member: userRelationship },
  ]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showValidationError, setShowValidationError] = useState(false);

  // Add these new state variables
  const [rateType, setRateType] = useState("hourly");
  const [hourlyRate, setHourlyRate] = useState(250);
  const [fixedRate, setFixedRate] = useState(250);

  const [showDateOfAccident, setShowDateOfAccident] = useState(false); // New state for Date of Accident
  const [dateOfAccident, setDateOfAccident] = useState(""); // State for the Date of Accident field

  // Add new state variables for Liability Law fields
  const [clientLicencePlate, setClientLicencePlate] = useState("");
  const [otherPartyLicencePlate, setOtherPartyLicencePlate] = useState("");
  const [insurancePolicyNumber, setInsurancePolicyNumber] = useState("");

  // Auto-save functions with comprehensive form data preservation
  const saveNow = () => {
    // Don't save if we're in the middle of restoring state
    if (isRestoringState) {
      console.log('⏸️ Skipping auto-save during state restoration');
      return;
    }

    const formState = {
      // Tab state and basic fields
      activeTab,
      type,
      status,
      reference,
      name,
      description,
      createdOn,
      eventDate,
      language,
      categories,
      // Office members and parties data
      officeMembers,
      parties,
      selectedRelationships,
      selectedParties,
      // Rate configuration
      rateType,
      hourlyRate,
      fixedRate,
      // Liability Law specific fields
      dateOfAccident,
      showDateOfAccident,
      clientLicencePlate,
      otherPartyLicencePlate,
      insurancePolicyNumber,
      opposingPartyFileNumber,
      // Form validation state
      formErrors,
      // Additional metadata
      lastSaved: new Date().toISOString(),
      saveOrigin: 'auto-save'
    };

    console.log("💾 Auto-saving form state to localStorage", {
      name: formState.name,
      activeTab: formState.activeTab,
      partiesCount: formState.parties?.length || 0,
      saveOrigin: formState.saveOrigin
    });

    try {
      localStorage.setItem('createFileFormState', JSON.stringify(formState));
      localStorage.setItem('createFileFormLastSaved', new Date().toISOString());
      console.log('✅ Auto-save successful');
    } catch (error) {
      console.error('❌ Auto-save failed:', error);
    }
  };

  // Function to save form state before navigating away
  const saveBeforeNavigation = (destination) => {
    const formState = {
      // Tab state and basic fields
      activeTab,
      type,
      status,
      reference,
      name,
      description,
      createdOn,
      eventDate,
      language,
      categories,
      // Office members and parties data
      officeMembers,
      parties,
      selectedRelationships,
      selectedParties,
      // Rate configuration
      rateType,
      hourlyRate,
      fixedRate,
      // Liability Law specific fields
      dateOfAccident,
      showDateOfAccident,
      clientLicencePlate,
      otherPartyLicencePlate,
      insurancePolicyNumber,
      opposingPartyFileNumber,
      // Form validation state
      formErrors,
      // Navigation metadata
      lastSaved: new Date().toISOString(),
      saveOrigin: 'navigation',
      navigationDestination: destination
    };

    console.log(`=== Saving form state before navigating to ${destination} ===`);
    console.log('Form state summary:', {
      activeTab: formState.activeTab,
      name: formState.name,
      partiesCount: formState.parties?.length || 0,
      officeMembersCount: formState.officeMembers?.length || 0,
      destination: destination
    });

    try {
      localStorage.setItem('createFileFormState', JSON.stringify(formState));
      // Set navigation context for returning from relationship creation
      localStorage.setItem('relationshipCreationStartTime', Date.now().toString());
      console.log('✓ Form state successfully saved to localStorage');
    } catch (error) {
      console.error('✗ Failed to save form state:', error);
      // Still continue with navigation even if save fails
    }
  };

  const clearSavedData = () => {
    return new Promise((resolve) => {
      localStorage.removeItem('createFileFormState');
      localStorage.removeItem('createFileFormLastSaved');
      localStorage.removeItem('newlyCreatedPartyId');
      localStorage.removeItem('partyCreationContext');
      localStorage.removeItem('relationshipCreationStartTime');
      console.log('All saved form data cleared');
      resolve();
    });
  };

  // Debug helper function - accessible from browser console
  window.debugFormState = () => {
    console.log('=== FORM STATE DEBUG INFO ===');
    console.log('sessionStorage.inPartyCreationFlow:', sessionStorage.getItem('inPartyCreationFlow'));
    console.log('localStorage.relationshipCreationStartTime:', localStorage.getItem('relationshipCreationStartTime'));
    console.log('localStorage.createFileFormState:', localStorage.getItem('createFileFormState') ? 'EXISTS' : 'NOT FOUND');
    console.log('localStorage.createFileFormLastSaved:', localStorage.getItem('createFileFormLastSaved'));
    console.log('isRestoringState:', isRestoringState);
    console.log('Current form data:', {
      activeTab,
      name,
      status,
      type,
      partiesCount: parties.length,
      officeMembersCount: officeMembers.length
    });

    // Show saved data if it exists
    const saved = localStorage.getItem('createFileFormState');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('Saved form data:', {
          activeTab: parsed.activeTab,
          name: parsed.name,
          status: parsed.status,
          type: parsed.type,
          partiesCount: parsed.parties?.length || 0,
          saveOrigin: parsed.saveOrigin,
          lastSaved: parsed.lastSaved
        });
      } catch (e) {
        console.error('Error parsing saved data:', e);
      }
    }
    console.log('=== END DEBUG INFO ===');
  };

  // Test function to manually trigger auto-save
  window.testAutoSave = () => {
    console.log('🧪 Testing manual auto-save...');
    saveNow();
  };

  // Test function to clear all saved data
  window.clearAllSavedData = () => {
    console.log('🗑️ Clearing all saved data...');
    clearSavedData().then(() => {
      sessionStorage.clear();
      console.log('✅ All data cleared');
    });
  };

  // useEffect(() => {
  //   console.log("user erlation", userRelationship);
  // }, [userRelationship]);

  // useEffect(() => {
  //   console.log("officemembers", officeMembers);
  // }, [officeMembers, userRelationship]);

  const typeOptions = [
    { value: "Liability Law", label: t('pages:files.types.liability_law') },
    { value: "Civil Law", label: t('pages:files.types.civil_law') },
    { value: "Labor Law", label: t('pages:files.types.labor_law') },
    { value: "Administrative Law", label: t('pages:files.types.administrative_law') },
    { value: "Tax Law", label: t('pages:files.types.tax_law') },
    { value: "Construction Law", label: t('pages:files.types.construction_law') },
    { value: "Rental & Residential Law", label: t('pages:files.types.rental_residential_law') },
    { value: "Environment & Spatial Planning Law", label: t('pages:files.types.environment_spatial_law') },
    { value: "Corporate Law", label: t('pages:files.types.corporate_law') },
    { value: "Military Law", label: t('pages:files.types.military_law') },
    { value: "Education Law", label: t('pages:files.types.education_law') },
    { value: "Other", label: t('pages:files.types.other') },
    { value: "Personal & Family Law", label: t('pages:files.types.personal_family_law') },
    { value: "Legal Entity Law", label: t('pages:files.types.legal_entity_law') },
    { value: "Social Security Law", label: t('pages:files.types.social_security_law') },
    { value: "Criminal Law", label: t('pages:files.types.criminal_law') },
    { value: "Diciplinary Law", label: t('pages:files.types.disciplinary_law') },
    { value: "Law of Obligations", label: t('pages:files.types.law_obligations') },
    { value: "Insurance Law", label: t('pages:files.types.insurance_law') },
    { value: "Immigration Law", label: t('pages:files.types.immigration_law') },
    { value: "Commercial Law", label: t('pages:files.types.commercial_law') },
  ];

  const [parties, setParties] = useState([{ type: "", name: "", address: "" }]);

//   const [parties, setParties] = useState(() => {
//   // Try to read from localStorage
//   const savedData = localStorage.getItem('createFileFormState'); // change to your actual key!
//   if (savedData) {
//     try {
//       const parsed = JSON.parse(savedData);
//       // If it has a valid 'parties' array with at least one item
//       if (parsed.parties && Array.isArray(parsed.parties)) {
//         return parsed.parties;
//       }
//     } catch (e) {
//       console.error("Failed to parse localStorage data:", e);
//     }
//   }
//   // Fallback if no valid data found
//   return [{ type: "", name: "", address: "" }];
// });

  // Memoized calculation of selected party values for better performance and debugging
  
  
  const selectedPartyValues = useMemo(() => {
    if (!Array.isArray(relationships) || !Array.isArray(parties)) {
      console.log('🔍 selectedPartyValues: relationships or parties not ready', {
        relationshipsReady: Array.isArray(relationships),
        partiesReady: Array.isArray(parties),
        relationshipsCount: relationships?.length || 0,
        partiesCount: parties?.length || 0
      });
      return parties.map(() => null);
    }

    const values = parties.map((party, index) => {
      if (!party.name) {
        return null;
      }

      const selectedRelationship = relationships.find(rel => rel._id === party.name);

      if (!selectedRelationship) {
        console.log(`⚠️ selectedPartyValues: Party ${index} has ID "${party.name}" but relationship not found`, {
          partyNameId: party.name,
          availableIds: relationships.map(r => r._id).slice(0, 10),
          relationshipsCount: relationships.length
        });
        return null;
      }

      console.log(`✅ selectedPartyValues: Party ${index} mapped successfully`, {
        partyNameId: party.name,
        relationshipName: selectedRelationship.name || selectedRelationship.businessName,
        relationshipType: selectedRelationship.type
      });

      return selectedRelationship;
    });

    return values;
  }, [relationships, parties]);




  // Initial relationships fetch on component mount
  useEffect(() => {
    console.log('🔄 Initial relationships fetch on component mount');
    dispatch(fetchRelationships());
  }, [dispatch]);

  // Detect when returning from relationship creation and refresh relationships
  useEffect(() => {
    const handleFocus = () => {
      const inPartyCreationFlow = sessionStorage.getItem('inPartyCreationFlow') === 'true';
      const lastCreatedRelationshipId = sessionStorage.getItem('lastCreatedRelationshipId');

      if (inPartyCreationFlow && lastCreatedRelationshipId) {
        console.log('🔄 Detected return from relationship creation, refreshing relationships...');
        dispatch(fetchRelationships());
      }
    };

    // Listen for window focus events (when user returns to the tab/page)
    window.addEventListener('focus', handleFocus);

    // Also check immediately when component mounts
    handleFocus();

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [dispatch]);

  // Debug relationships array changes
  useEffect(() => {
    console.log('📊 Relationships array updated:', {
      count: relationships?.length || 0,
      firstFew: relationships?.slice(0, 3).map(r => ({
        id: r._id,
        name: r.name || r.businessName,
        type: r.type
      })) || []
    });
  }, [relationships]);

  // Debug selectedPartyValues changes
  useEffect(() => {
    console.log('🎯 selectedPartyValues updated:', {
      count: selectedPartyValues.length,
      values: selectedPartyValues.map((value, index) => ({
        index,
        hasValue: !!value,
        id: value?._id,
        name: value?.name || value?.businessName,
        type: value?.type
      }))
    });
  }, [selectedPartyValues]);

  // Handle returning from relationship creation with enhanced timing and debugging
  useEffect(() => {
    // Check if we're returning from relationship creation
    const creationStartTime = localStorage.getItem('relationshipCreationStartTime');
    const createdRelationship = sessionStorage.getItem('createdRelationship');
    const inPartyCreationFlow = sessionStorage.getItem('inPartyCreationFlow');
    const lastCreatedRelationshipId = sessionStorage.getItem('lastCreatedRelationshipId');

    console.log('🔍 Checking return from relationship creation:', {
      creationStartTime: !!creationStartTime,
      createdRelationship,
      inPartyCreationFlow,
      lastCreatedRelationshipId,
      currentRelationshipsCount: relationships?.length || 0
    });

    if (creationStartTime && createdRelationship === 'true') {
      console.log('=== 🎯 CONFIRMED: Returning from relationship creation ===');
      console.log('creationStartTime:', creationStartTime);
      console.log('createdRelationship:', createdRelationship);
      console.log('inPartyCreationFlow:', inPartyCreationFlow);
      console.log('lastCreatedRelationshipId:', lastCreatedRelationshipId);

      // Clear the navigation flags but keep the ID for now
      localStorage.removeItem('relationshipCreationStartTime');
      sessionStorage.removeItem('createdRelationship');
      sessionStorage.removeItem('inPartyCreationFlow');

      // Enhanced relationship refresh with multiple attempts and timing
      console.log('🔄 Starting enhanced relationship refresh sequence...');

      // Immediate fetch
      dispatch(fetchRelationships());

      // Additional fetches with delays to ensure backend synchronization
      setTimeout(() => {
        console.log('🔄 Second relationship fetch (500ms delay)');
        dispatch(fetchRelationships());
      }, 500);

      setTimeout(() => {
        console.log('🔄 Third relationship fetch (1000ms delay)');
        dispatch(fetchRelationships());
      }, 1000);

      // Final fetch with longer delay as fallback
      setTimeout(() => {
        console.log('🔄 Final relationship fetch (2000ms delay)');
        dispatch(fetchRelationships());

        // Also check if we have the relationship ID and try to find it
        const lastCreatedId = sessionStorage.getItem('lastCreatedRelationshipId');
        if (lastCreatedId) {
          console.log('🔍 Final check for relationship ID:', lastCreatedId);
        }
      }, 2000);
      
      // Show success message
      setNotification({
        open: true,
        message: t('pages:files.new_party_created'),
        severity: "success",
      });
    }

    // Restore form state from localStorage if it exists
    const savedFormState = localStorage.getItem('createFileFormState');
    const lastSaved = localStorage.getItem('createFileFormLastSaved');

    console.log('🔍 Checking for saved form state on component mount...');
    console.log('savedFormState exists:', !!savedFormState);
    console.log('lastSaved:', lastSaved);

    if (savedFormState) {
      try {
        const formState = JSON.parse(savedFormState);
        setIsRestoringState(true);
        console.log('=== Restoring saved form state ===');
        console.log('Form state summary:', {
          activeTab: formState.activeTab,
          name: formState.name,
          partiesCount: formState.parties?.length || 0,
          officeMembersCount: formState.officeMembers?.length || 0,
          saveOrigin: formState.saveOrigin,
          navigationDestination: formState.navigationDestination,
          lastSaved: formState.lastSaved
        });

        // Restore all form fields with proper fallbacks
        setActiveTab(formState.activeTab ?? 0);
        setType(formState.type ?? "");
        setStatus(formState.status ?? "");
        setReference(formState.reference ?? "");
        setName(formState.name ?? "");
        setDescription(formState.description ?? "");
        setCreatedOn(formState.createdOn ?? "");
        setEventDate(formState.eventDate ?? "");
        setLanguage(formState.language ?? "English");
        setCategories(formState.categories ?? "");

        // Only restore office members if we have data and handle default state
        if (formState.officeMembers && formState.officeMembers.length > 0) {
          setOfficeMembers(formState.officeMembers);
        } else {
          setOfficeMembers([{ role: "Responsible Lawyer", member: userRelationship }]);
        }

        // Restore parties with fallback
        setParties(formState?.parties ?? [{ type: "", name: "", address: "" }]);

        // Restore selections if available
        if (formState.selectedRelationships) {
          setSelectedRelationships(formState.selectedRelationships);
        }

        if (formState.selectedParties) {
          setSelectedParties(formState.selectedParties);
        }
        
        // Restore rate configuration
        setRateType(formState.rateType ?? "hourly");
        setHourlyRate(formState.hourlyRate ?? 250);
        setFixedRate(formState.fixedRate ?? 0);
        
        // Restore Liability Law fields
        setDateOfAccident(formState.dateOfAccident ?? "");
        setShowDateOfAccident(formState.showDateOfAccident ?? false);
        setClientLicencePlate(formState.clientLicencePlate ?? "");
        setOtherPartyLicencePlate(formState.otherPartyLicencePlate ?? "");
        setInsurancePolicyNumber(formState.insurancePolicyNumber ?? "");
        setOpposingPartyFileNumber(formState.opposingPartyFileNumber ?? "");
        
        // Restore form errors
        setFormErrors(formState.formErrors ?? {});

        // Don't clear the saved state yet - keep it for potential further navigation
        console.log('✓ Form state restoration complete');
      } catch (error) {
        console.error("Error restoring form state:", error);
        // Clean up localStorage in case of corruption
        clearSavedData().then(() => {
          setIsRestoringState(false);
        });
      }
    }
  }, [dispatch, userRelationship]);

  // Set default responsible lawyer if no saved form state
  useEffect(() => {
    if (user && relationships && !localStorage.getItem('createFileFormState')) {
      const defaultMember = relationships.find((rel) => rel._id === user.relationship);
      if (defaultMember) {
        setOfficeMembers([{ role: "Responsible Lawyer", member: defaultMember._id }]);
        setSelectedRelationships([defaultMember]);
      }
    }
  }, [user, relationships]);

  // Simplified relationship synchronization after returning from relationship creation
  useEffect(() => {
    const lastCreatedRelationshipId = sessionStorage.getItem('lastCreatedRelationshipId');
    const inPartyCreationFlow = sessionStorage.getItem('inPartyCreationFlow') === 'true';

    if (lastCreatedRelationshipId && relationships && relationships.length > 0 && inPartyCreationFlow) {
      console.log('🔍 Processing newly created relationship:', {
        lastCreatedRelationshipId,
        relationshipsCount: relationships?.length || 0,
        availableIds: Array.isArray(relationships) ? relationships.map(r => r._id).slice(0, 5) : []
      });

      // Find the newly created relationship in our relationships array
      const newlyCreatedRelationship = relationships.find(
        rel => rel._id === lastCreatedRelationshipId
      );

      if (newlyCreatedRelationship) {
        console.log('✅ Found newly created relationship:', {
          id: newlyCreatedRelationship._id,
          name: newlyCreatedRelationship.name || newlyCreatedRelationship.businessName,
          type: newlyCreatedRelationship.type
        });

        // Check if we were on the parties tab when we left
        const savedFormState = localStorage.getItem('createFileFormState');
        if (savedFormState) {
          try {
            const formState = JSON.parse(savedFormState);

            // If we were on the parties tab, auto-select the new relationship
            if (formState.activeTab === 2) {
              // Find the first party that has no name selected
              const emptyPartyIndex = parties.findIndex(party => !party.name);

              if (emptyPartyIndex !== -1) {
                // Update the existing empty party with the new relationship
                const updatedParties = [...parties];
                updatedParties[emptyPartyIndex] = {
                  ...updatedParties[emptyPartyIndex],
                  name: newlyCreatedRelationship._id,
                  type: formState.parties?.[emptyPartyIndex]?.type || updatedParties[emptyPartyIndex].type || "Client" // Preserve type
                };

                console.log(`🎯 Updating party at index ${emptyPartyIndex} with relationship:`, {
                  partyIndex: emptyPartyIndex,
                  relationshipId: newlyCreatedRelationship._id,
                  relationshipName: newlyCreatedRelationship.name || newlyCreatedRelationship.businessName
                });

                // Update parties state
                setParties(updatedParties);

                console.log(`✅ Auto-selected new relationship for party at index ${emptyPartyIndex}`);

                // Show success notification
                setNotification({
                  open: true,
                  message: `New party "${newlyCreatedRelationship.name || newlyCreatedRelationship.businessName}" has been selected!`,
                  severity: "success",
                });
              } else {
                // Add a new party with the new relationship
                const newParty = {
                  type: "Client", // Default type
                  name: newlyCreatedRelationship._id,
                  address: ""
                };

                console.log('🎯 Adding new party with relationship:', {
                  relationshipId: newlyCreatedRelationship._id,
                  relationshipName: newlyCreatedRelationship.name || newlyCreatedRelationship.businessName
                });

                const updatedParties = [...parties, newParty];
                setParties(updatedParties);

                console.log('✅ Added new party with the created relationship');

                // Show success notification
                setNotification({
                  open: true,
                  message: `New party "${newlyCreatedRelationship.name || newlyCreatedRelationship.businessName}" has been added!`,
                  severity: "success",
                });
              }
            }
          } catch (error) {
            console.error('❌ Error parsing saved form state:', error);
          }
        }

        // Clean up session storage flags
        sessionStorage.removeItem('lastCreatedRelationshipId');
        sessionStorage.removeItem('inPartyCreationFlow');
        sessionStorage.removeItem('relationshipCreationTimestamp');
        sessionStorage.removeItem('relationshipCreatedSuccessfully');
        console.log('🧹 Cleaned up relationship creation flags');
      } else {
        console.log('⏳ Newly created relationship not yet available, will retry...', {
          searchingFor: lastCreatedRelationshipId,
          availableIds: Array.isArray(relationships) ? relationships.map(r => r._id) : []
        });
        // Simple retry mechanism - fetch relationships once more
        setTimeout(() => {
          console.log('🔄 Retrying relationship fetch...');
          dispatch(fetchRelationships());
        }, 1000);
      }
    }
  }, [relationships, parties, dispatch]);


  
  // Set up auto-save functionality
  useEffect(() => {
    // Don't trigger auto-save during form restoration
    if (isRestoringState) return;
    
    // Auto-save the form every 30 seconds
    const autoSaveInterval = setInterval(() => {
      saveNow();
    }, 30000);
    
    // Set up beforeunload event handler to save form when navigating away
    const handleBeforeUnload = () => {
      saveNow();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up on component unmount
    return () => {
      clearInterval(autoSaveInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    // Dependencies for auto-save - any major form state change should trigger a save
    activeTab, name, status, type, reference, description, 
    parties, officeMembers, selectedRelationships, selectedParties,
    rateType, hourlyRate, fixedRate, showDateOfAccident, dateOfAccident,
    clientLicencePlate, otherPartyLicencePlate, insurancePolicyNumber, 
    opposingPartyFileNumber, isRestoringState
  ]);
  
  // Component unmount cleanup - ensure we don't leave stale data if navigating without cancel/submit
  useEffect(() => {
    return () => {
      // Check if we're navigating to relationship creation (don't clean up in that case)
      const navigatingToRelationshipCreate = sessionStorage.getItem('inPartyCreationFlow') === 'true';
      const hasRelationshipCreationStartTime = localStorage.getItem('relationshipCreationStartTime') !== null;

      console.log('=== Component unmounting - checking cleanup conditions ===');
      console.log('inPartyCreationFlow:', navigatingToRelationshipCreate);
      console.log('hasRelationshipCreationStartTime:', hasRelationshipCreationStartTime);

      // More robust check: if either flag indicates we're in party creation flow, preserve data
      if (!navigatingToRelationshipCreate && !hasRelationshipCreationStartTime) {
        // We're navigating away, not to relationship creation, so clean up
        console.log('✓ Component unmounting - cleaning up saved form data');
        localStorage.removeItem('createFileFormState');
        localStorage.removeItem('createFileFormLastSaved');
        localStorage.removeItem('newlyCreatedPartyId');
        localStorage.removeItem('partyCreationContext');
        localStorage.removeItem('relationshipCreationStartTime');
        sessionStorage.removeItem('inPartyCreationFlow');
        sessionStorage.removeItem('createdRelationship');
        sessionStorage.removeItem('lastCreatedRelationshipId');
      } else {
        console.log('✓ Component unmounting - preserving form data (in party creation flow)');
      }
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddOfficeMember = () => {
    setOfficeMembers([...officeMembers, { role: "", member: "" }]);
    setSelectedRelationships([...selectedRelationships, null]);
  };

  const handleDeleteOfficeMember = (index) => {
    const newMembers = officeMembers.filter((_, i) => i !== index);
    setOfficeMembers(newMembers);
    const newSelectedRelationships = selectedRelationships.filter(
      (_, i) => i !== index
    );
    setSelectedRelationships(newSelectedRelationships);
  };

  const handleAddParty = () => {
    setParties([...parties, { type: "", name: "", address: "" }]);
    setSelectedRelationships([...selectedRelationships, null]);
  };

  const handleDeleteParty = (index) => {
    const newParties = parties.filter((_, i) => i !== index);
    setParties(newParties);
  };

  // const handleFieldChange = (field, value) => {
  //   setFormErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  //   switch (field) {
  //     case "name":
  //       setName(value);
  //       break;
  //     case "status":
  //       setStatus(value);
  //       break;
  //     case "type":
  //       setType(value);
  //       break;
  //     default:
  //       break;
  //   }
  // };
  const handleFieldChange = (field, value) => {
    setFormErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
    switch (field) {
      case "name":
        setName(value);
        break;
      case "status":
        setStatus(value);
        break;
      case "type":
        setType(value);
        setShowDateOfAccident(value === "Liability Law"); // Show Date of Accident if Liability Law is selected
        break;
      default:
        break;
    }
  };

  const handleMemberChange = (index, newValue) => {
    const newMembers = [...officeMembers];
    newMembers[index].member = newValue ? newValue._id : "";
    setOfficeMembers(newMembers);

    const newSelectedRelationships = [...selectedRelationships];
    newSelectedRelationships[index] = newValue;
    setSelectedRelationships(newSelectedRelationships);

    setFormErrors((prevErrors) => ({ ...prevErrors, officeMembers: "" }));
  };

  // useEffect(() => {
  //   if (user && relationships) {
  //     setUserRole(user.role);
  //     setSelectedRelationships(
  //       officeMembers.map((member, index) =>
  //         index === 0 && userRole === "Responsible Lawyer"
  //           ? relationships?.find((rel) => rel._id === user.relationship) || null
  //           : relationships?.find((rel) => rel._id === member.member) || null
  //       )
  //     );
  //   }
  // }, [user, relationships, officeMembers]);

  // useEffect(() => {
  //   if (user && relationships) {
  //     setUserRole(user.role);
  //     setUserRelationship(user.relationship);
  //     if (officeMembers.length === 1 && userRole === "Responsible Lawyer") {
  //       setOfficeMembers([
  //         { role: "Responsible Lawyer", member: user.relationship },
  //       ]);
  //     }
  //   }
  // }, [user, relationships]);

  const { loading, success, error } = useSelector((state) => state.fileCreate);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t('pages:files.file_created_successfully'),
        severity: "success",
      });
      dispatch({ type: "FILE_CREATE_RESET" }); // Reset the success state
      dispatch(fetchFiles());
      // window.location.reload(true);
      // navigate('/documents');
    } else if (error) {
      setNotification({
        open: true,
        message: error,
        severity: "error",
      });
      dispatch({ type: "FILE_CREATE_RESET" }); // Reset the error state
    }
  }, [success, error, dispatch]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Modify handleCreateFile to include rate config
  const handleCreateFile = async () => {
    const errors = {};

    // Validate required fields
    if (!name.trim()) errors.name = t('pages:files.errors.name_required');
    if (!status) errors.status = t('pages:files.errors.status_required');
    if (!type) errors.type = t('pages:files.errors.jurisdiction_required');

    // Validate office members
    const invalidOfficeMembers = officeMembers.some(
      (member) => !member.role || !member.member
    );
    if (invalidOfficeMembers) {
      errors.officeMembers = t('pages:files.errors.office_members_required');
    }

    // Validate parties
    const invalidParties = parties.some((party) => !party.type || !party.name);
    if (invalidParties) {
      errors.parties = t('pages:files.errors.parties_required');
    }

    // If there are validation errors
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setShowValidationError(true);
      setNotification({
        open: true,
        message: t('pages:files.errors.fill_required_fields'),
        severity: "error",
      });
      return;
    }

    const newFile = {
      type,
      reference,
      name,
      status,
      description,
      createdOn,
      eventDate,
      language,
      categories,
      officeMembers,
      parties,
      opposingPartyFileNumber,
      rateConfig: {
        type: rateType,
        hourlyRate: rateType === "hourly" ? hourlyRate : 0,
        fixedRate: rateType === "fixed" ? fixedRate : 0,
      },
      dateOfAccident: showDateOfAccident ? dateOfAccident : undefined, // Include only if applicable
      clientLicencePlate: showDateOfAccident ? clientLicencePlate : undefined,
      otherPartyLicencePlate: showDateOfAccident ? otherPartyLicencePlate : undefined,
      insurancePolicyNumber: showDateOfAccident ? insurancePolicyNumber : undefined,
    };

    console.log("Creating new file:", newFile);

    // Clear all saved form data, including session storage and any flags
    const cleanupStorage = async () => {
      try {
        // First clear localStorage form data
        await clearSavedData();
        
        // Then clear any session storage we might have used
        sessionStorage.removeItem('inPartyCreationFlow');
        sessionStorage.removeItem('createdRelationship');
        
        console.log('All form data and navigation flags cleared before file creation');
      } catch (error) {
        console.warn('Error cleaning up storage before file creation:', error);
      }
    };

    try {
      await cleanupStorage();
      
      // Create the file (folders will be automatically created in the backend)
      await dispatch(createFile(newFile));
      
      console.log('✅ File created successfully with default folders');
        
        // Navigate back after a short delay to allow the notification to be seen
        setTimeout(() => {
          navigate(-1);
        }, 1000);
    } catch (error) {
      console.error('Failed to create file:', error);
      setNotification({
        open: true,
        message: error.response?.data?.message || t('pages:files.errors.create_failed'),
        severity: "error",
      });
    }
  };

  const handlePartyChange = (index, newValue) => {
    const newParties = [...parties];
    newParties[index].name = newValue ? newValue._id : "";
    setParties(newParties);

    setFormErrors((prevErrors) => ({ ...prevErrors, parties: "" }));
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleAddNewRelationship = () => {
    console.log('=== Starting party creation flow ===');
    console.log('Current form state before navigation:', {
      activeTab,
      name,
      partiesCount: parties.length,
      officeMembersCount: officeMembers.length
    });

    // CRITICAL: Set the navigation flag FIRST, before any other operations
    // This prevents race conditions with the cleanup effect
    sessionStorage.setItem('inPartyCreationFlow', 'true');
    console.log('✓ Party creation flow flag set in sessionStorage');

    // Verify the flag was set correctly
    const flagCheck = sessionStorage.getItem('inPartyCreationFlow');
    console.log('Flag verification check:', flagCheck);

    // Save all form state with navigation metadata before redirecting
    saveBeforeNavigation('/relationships/create');
    console.log('✓ Form state saved before navigation');

    // Verify localStorage was set
    const savedState = localStorage.getItem('createFileFormState');
    console.log('Saved state verification:', savedState ? 'EXISTS' : 'NOT FOUND');

    // Additional debugging - check if localStorage content is valid
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        console.log('Saved state validation - activeTab:', parsed.activeTab, 'name:', parsed.name);
        console.log('Saved state validation - parties count:', parsed.parties?.length || 0);
      } catch (e) {
        console.error('❌ Saved state is corrupted:', e);
      }
    }

    // Add a small delay to ensure sessionStorage is properly set
    // This helps prevent race conditions during component unmount
    setTimeout(() => {
      console.log('✓ Navigating to relationship creation page');
      console.log('Final flag check before navigation:', sessionStorage.getItem('inPartyCreationFlow'));
      navigate("/relationships/create");
    }, 10);
  };

  const labelStyles = {
    "& .MuiFormControlLabel-label": {
      fontSize: "10.5px",
      fontFamily:
        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
      fontWeight: 400,
    },
  };

  return userRole === "Assistant Lawyer" ? (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:files.create_new')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <Typography variant="h6" component="h1">
          You are not authorized to view this page.
        </Typography>
      </Box>
    </Container>
  ) : (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={t('pages:files.create_new')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />
      <Box
        sx={{
          backgroundColor: "white",
          padding: "1px 24px 24px 24px ",
          margin: "24px 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "white",
            marginTop: "24px",
            border: "1px solid #d1d1d1",
          }}
        >
          <Box sx={{ width: "200px", borderRight: "1px solid #d1d1d1" }}>
            <Box sx={{ marginBottom: "40px", marginTop: "30px" }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                orientation="vertical"
                aria-label="relationship tabs"
                TabIndicatorProps={{
                  sx: {
                    left: 0, // Position the indicator at the start
                  },
                }}
                sx={verticalTabStyles}
              >
                <Tab label={t('pages:files.tabs.general')} />
                <Tab label={t('pages:files.tabs.office_members')} />
                <Tab label={t('pages:files.tabs.parties')} />
                <Tab label={t('pages:files.tabs.rates')} />
              </Tabs>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              paddingLeft: 5,
              paddingTop: 6,
              width: "calc(100% - 200px)",
            }}
          >
            {activeTab === 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "1rem",
                }}
              >
                <TextField
                  label={t('forms:labels.fileName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  size="small"
                  required
                  sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                />
                
                

                {/* <TextField
                label="Reference"
                variant="standard"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
              /> */}

                <FormControl
                  variant="outlined"
                  size="small"
                  required
                  sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                >
                  <InputLabel id="status-select-filled-label">
                    {t('forms:labels.fileStatus')}
                  </InputLabel>
                  <Select
                    labelId="status-select-filled-label"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    size="small"
                    label={t('forms:labels.fileStatus')}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="Active">{t('common:active')}</MenuItem>
                    <MenuItem value="Inactive">{t('common:inactive')}</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  required
                  sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                >
                  <Autocomplete
                    options={typeOptions}
                    getOptionLabel={(option) => option.label || option}
                    value={typeOptions.find(opt => opt.value === type) || null}
                    onChange={(event, newValue) => {
                      // Direct handling of jurisdiction change
                      const selectedValue = newValue?.value || newValue;
                      setType(selectedValue);
                      // Explicitly handle showing Liability Law fields
                      const showFields = selectedValue === "Liability Law";
                      console.log("Setting jurisdiction to:", selectedValue);
                      console.log("Should show liability fields:", showFields);
                      setShowDateOfAccident(showFields);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('pages:files.jurisdiction')}
                        variant="outlined"
                        size="small"
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </FormControl>
                                  {/* Liability Law fields with proper spacing */}
                  {console.log("Current jurisdiction:", type, "showDateOfAccident:", showDateOfAccident)}
                  {showDateOfAccident && (
                    <>
                      <TextField
                        label={t('pages:files.date_of_accident')}
                        variant="outlined"
                        type="date"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={dateOfAccident}
                        onChange={(e) => setDateOfAccident(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                      <TextField
                        label={t('pages:files.client_licence_plate')}
                        variant="outlined"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={clientLicencePlate}
                        onChange={(e) => setClientLicencePlate(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                      <TextField
                        label={t('pages:files.other_party_licence_plate')}
                        variant="outlined"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={otherPartyLicencePlate}
                        onChange={(e) => setOtherPartyLicencePlate(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                      <TextField
                        label={t('pages:files.insurance_policy_number')}
                        variant="outlined"
                        size="small"
                        sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                        value={insurancePolicyNumber}
                        onChange={(e) => setInsurancePolicyNumber(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                      
                    </>
                  )}
                  <TextField
                      label={t('pages:files.opposing_party_file_number')}
                      variant="outlined"
                      size="small"
                      sx={{...textFieldStyles("30%"), marginBottom: "16px"}}
                      value={opposingPartyFileNumber}
                      onChange={(e) => setOpposingPartyFileNumber(e.target.value)}
                      />

                {/* <TextField
                label="Created On"
                variant="standard"
                type="date"
                value={createdOn}
                onChange={(e) => setCreatedOn(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label={t('pages:files.event_date')}
                variant="standard"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <FormControl variant="standard" sx={{ mr: 3, width: "30%" }}>
                <InputLabel id="language-select-label">{t('forms:language')}</InputLabel>
                <Select
                  labelId="language-select-label"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  label={t('forms:language')}
                >
                  <MenuItem value="English">{t('common:languages.english')}</MenuItem>
                  <MenuItem value="Dutch">{t('common:languages.dutch')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t('forms:categories')}
                variant="standard"
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
              />
              <TextField
                label={t('common:description')}
                variant="standard"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                size="small"
                sx={{
                  marginRight: 3,
                  width: "30%",
                  marginTop: "0",
                  marginBottom: "0",
                }}
              /> */}
              </Box>
            )}
            {activeTab === 1 && (
              <Box>
                {officeMembers.map((member, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={textFieldStyles("30%")}
                      required
                    >
                      <InputLabel id={`role-select-label-${index}`}>
                        Role
                      </InputLabel>
                      <Select
                        labelId={`role-select-label-${index}`}
                        value={member.role}
                        onChange={(e) => {
                          const newMembers = [...officeMembers];
                          newMembers[index].role = e.target.value;
                          setOfficeMembers(newMembers);
                        }}
                        label={t('forms:labels.role')}
                      // disabled={
                      //   index === 0 && userRole === "Responsible Lawyer"
                      // } // Disable the first role if user is Responsible Lawyer
                      >
                        <MenuItem value="Responsible Lawyer">
                          {t('pages:files.roles.responsible_lawyer')}
                        </MenuItem>
                        <MenuItem value="Treating Lawyer">
                          {t('pages:files.roles.treating_lawyer')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={{
                        width: "35%",
                        color: "blue",
                      }}
                      required
                    >
                      <CustomAutocomplete
                        options={[
                          { _id: "add-member", contractType: "AddNewIdentity" },
                          ...(Array.isArray(relationships) ? relationships : []),
                        ]}
                        showAddNewOption={true}
                        value={selectedRelationships[index] || null}
                        onChange={(event, newValue) => {
                          if (newValue && newValue._id === "add-member") {
                            handleAddNewRelationship();
                          } else {
                            handleMemberChange(index, newValue);
                          }
                        }}
                        label={t('forms:labels.member')}
                        filterType="Member"
                        addNewLabel={t('pages:files.add_new_lawyer')}
                        addNewPath="/relationships/create"
                        index={index}
                      // disabled={
                      //   member.role === "Responsible Lawyer" &&
                      //   userRole !== "Manager"
                      // }
                      />
                    </FormControl>
                    <IconButton
                      color="secondary"
                      onClick={() => handleDeleteOfficeMember(index)}
                      sx={{ marginLeft: 1 }}
                    // disabled={index === 0 && userRole === "Responsible Lawyer"} // Disable delete button for the first member if user is Responsible Lawyer
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formErrors.officeMembers && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formErrors.officeMembers}
                  </Typography>
                )}
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  color="primary"
                  onClick={handleAddOfficeMember}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('pages:files.add_office_member')}
                </Button>
              </Box>
            )}
            {activeTab === 2 && (
              <Box>
                {parties.map((party, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={textFieldStyles("30%")}
                      required
                    >
                      <InputLabel id={`party-type-select-label-${index}`}>
                        {t('forms:labels.type')}
                      </InputLabel>
                      <Select
                        labelId={`party-type-select-label-${index}`}
                        value={party.type}
                        onChange={(e) => {
                          const newParties = [...parties];
                          newParties[index].type = e.target.value;
                          setParties(newParties);
                        }}
                        label={t('forms:labels.type')}
                      >
                        <MenuItem value="Client">{t('pages:files.party_types.client')}</MenuItem>
                        <MenuItem value="Opposite Party">
                          {t('pages:files.party_types.opposite_party')}
                        </MenuItem>
                        <MenuItem value="Paying Party">{t('pages:files.party_types.paying_party')}</MenuItem>
                        <MenuItem value="Advocate Opposite Party">
                          {t('pages:files.party_types.advocate_opposite_party')}
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl
                      variant="standard"
                      sx={{ mr: 3, width: "30%" }}
                      required
                    >
                      <CustomAutocomplete
                        key={`party-${index}-${party.name}-${relationships?.length || 0}-${selectedPartyValues[index]?._id || 'none'}`}
                        options={Array.isArray(relationships) ? relationships : []}
                        value={selectedPartyValues[index] || null}
                        required={true}
                        onChange={(event, newValue) => {
                          console.log(`🎯 Party ${index} onChange triggered:`, {
                            newValue: newValue ? {
                              id: newValue._id,
                              name: newValue.name || newValue.businessName,
                              type: newValue.type
                            } : null,
                            previousValue: selectedPartyValues[index] ? {
                              id: selectedPartyValues[index]._id,
                              name: selectedPartyValues[index].name || selectedPartyValues[index].businessName
                            } : null
                          });

                          // Let CustomAutocomplete handle the add new option internally
                          handlePartyChange(index, newValue);
                        }}
                        label={t('forms:labels.fullName')}
                        filterType="Party"
                        addNewLabel={t('pages:files.add_new_party')}
                        addNewPath="/relationships/create"
                        index={index}
                        showAddNewOption={true}
                        onAddNew={handleAddNewRelationship}
                      />
                    </FormControl>
                    {/* <TextField
                    label="Address"
                    variant="standard"
                    value={party.address}
                    onChange={(e) => {
                      const newParties = [...parties];
                      newParties[index].address = e.target.value;
                      setParties(newParties);
                    }}
                    margin="normal"
                    size="small"
                    sx={{
                      marginRight: 3,
                      width: "30%",
                      marginTop: "0",
                      marginBottom: "0",
                    }}
                  /> */}
                    <IconButton
                      color="secondary"
                      onClick={() => handleDeleteParty(index)}
                      sx={{ marginLeft: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formErrors.parties && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    {formErrors.parties}
                  </Typography>
                )}
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  color="primary"
                  onClick={handleAddParty}
                  sx={{ marginRight: 3, textTransform: "capitalize" }}
                >
                  {t('pages:files.add_party')}
                </Button>
              </Box>
            )}
            {activeTab === 3 && (
              <Box>
                <FormControl
                  component="fieldset"
                  required
                  sx={{ marginBottom: 2, width: "100%" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      fontSize: "10.5px",
                      fontFamily:
                        "Fira Sans, Helvetica Neue, Helvetica, Roboto, Arial, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    {t('pages:files.rate_type')}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={rateType}
                    onChange={(e) => setRateType(e.target.value)}
                  >
                    <FormControlLabel
                      sx={labelStyles}
                      value="hourly"
                      control={<Radio />}
                      label={t('pages:files.hourly_rate')}
                    />
                    <FormControlLabel
                      sx={labelStyles}
                      value="fixed"
                      control={<Radio />}
                      label={t('pages:files.fixed_rate')}
                    />
                    <FormControlLabel
                      sx={labelStyles}
                      value="proBono"
                      control={<Radio />}
                      label={t('pages:files.pro_bono')}
                    />
                  </RadioGroup>
                </FormControl>

                {rateType === "hourly" && (
                  <TextField
                    label={t('pages:files.hourly_rate_euro')}
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(Number(e.target.value))}
                    sx={textFieldStyles("30%")}
                    size="small"
                    defaultValue={250}
                  />
                )}

                {rateType === "fixed" && (
                  <TextField
                    label={t('pages:files.fixed_rate_euro')}
                    type="number"
                    value={fixedRate}
                    onChange={(e) => setFixedRate(Number(e.target.value))}
                    sx={textFieldStyles("30%")}
                    size="small"
                    defaultValue={250}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            // padding: "10px 16px",
            // background: "#fff",
            zIndex: 10,
            backgroundColor: "white",
            color: "#000000",
            padding: "13px 80px",
          }}
        >
          <CancelButton
            variant="outlined"
            onClick={() => {
              // First, check if we have any saved form data
              const hasSavedData = localStorage.getItem('createFileFormState') !== null;
              
              // Clear all auto-saved data before canceling
              clearSavedData().then(() => {
                console.log('Auto-saved data cleared on cancel');
                
                // Also clear any session storage flags we might have set
                sessionStorage.removeItem('inPartyCreationFlow');
                sessionStorage.removeItem('createdRelationship');
                
                // Navigate back to files list
                navigate("/files");
              }).catch(error => {
                console.warn('Failed to clear auto-saved data on cancel:', error);
                navigate("/files"); // Navigate anyway
              });
            }}
            size="small"
          >
            {t('buttons:secondary.cancel')}
          </CancelButton>
          <SubmitButton onClick={handleCreateFile} size="small">
            {loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              t('pages:files.create_new')
            )}
          </SubmitButton>
        </Box>
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default CreateFilePage;
